import type { Faq as FaqType } from 'libs/http/api/content/content.types';

type Translate = (key: string) => string;

export const getFaqItems = (t: Translate): FaqType[] => [
  {
    id: 'what-is-regis',
    question: t('What exactly is Regis?'),
    answer: t(
      "A query interface over Global Database's registry dataset. You send a question as a string, Regis resolves which companies and fields it concerns, retrieves those fields from primary government registry records, and returns an answer with the source of each value.",
    ),
    default_opened: true,
  },
  {
    id: 'general-model',
    question: t('How is this different from asking a general-purpose model?'),
    answer: t(
      'A general model answers from training data, which has no filing behind it and no date attached. Regis has no company facts in its weights. It answers from records retrieved on the request, and returns those records with the answer so the reviewer can check it.',
    ),
  },
  {
    id: 'missing-field',
    question: t('What happens when the register does not publish the field?'),
    answer: t(
      'Regis returns the gap and names the reason. It will not substitute an estimate, a modelled figure or a value from a similar company.',
    ),
  },
  {
    id: 'coverage',
    question: t('Which countries are covered?'),
    answer: t(
      '400+ government registries across 200+ countries and territories. Field availability varies by jurisdiction because registers publish different things. Coverage by field and country is set out in the data dictionary.',
    ),
  },
  {
    id: 'own-product',
    question: t('Can I use it inside my own product?'),
    answer: t(
      'Yes, under a redistribution licence. Standard API access covers internal use. Serving the data to your own customers is licensed separately so the terms are explicit rather than assumed.',
    ),
  },
  {
    id: 'ai-agent',
    question: t('Does it work with an AI agent?'),
    answer: t(
      'Yes. The same dataset is exposed through an MCP server, so an assistant or internal agent can call it as a tool and receive the same sourced answers.',
    ),
  },
  {
    id: 'currency',
    question: t('How current is the data?'),
    answer: t(
      'Records are synced daily. Because registers publish on their own schedules, the response carries a retrieval timestamp so you know what the register held at the time of the read.',
    ),
  },
  {
    id: 'auth',
    question: t('How do I authenticate?'),
    answer: t(
      'A token in the Authorization header: <code>Authorization: Token YOUR_API_KEY</code>. Keys are issued from your account profile.',
    ),
  },
  {
    id: 'structured-endpoints',
    question: t('Can I still call the structured endpoints?'),
    answer: t(
      'Yes. Regis sits alongside the v2 API rather than replacing it. KYB, officers, shareholders, group structure, financials, credit reports, bank verification and enrichment all remain available with fixed response shapes.',
    ),
  },
  {
    id: 'security-docs',
    question: t('What security and compliance documentation is available?'),
    answer: t(
      'ISO 27001 certification and UK ICO registration. Security documentation and the data processing agreement are available under NDA before contract.',
    ),
  },
];
